<template>
  <!--==== PlanandCases ====-->
  <div class="page_header">
    <h1>{{ content.plan_and_cases_title }}</h1>
  </div>

  <div :class="['plan_and_cases_home', language === 'cn' ? 'font-cn' : 'font-en']">
    <div class="plan_and_cases_container">
      <div class="plan_and_cases_grid">
        <div
          v-for="sector in content.sectors"
          :key="sector.name"
          @click="toggleCases(sector.name)"
          :class="['sector', { active: activeSector === sector.name }]"
        >
          <img :src="sector.image" class="plan_and_cases_img" />
          <div class="sector_overlay">
            <span class="sector_overlay_name">{{ sector.displayName }}</span>
            <span class="sector_overlay_hint" v-if="activeSector === sector.name">
              {{ language === 'cn' ? '已选中' : 'Selected' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade-slide">
      <section v-if="activeSector" ref="casesSection" class="cases_section">
        <div class="cases_section_title">
          <h2>{{ activeSectorDisplayName }}</h2>
          <span class="cases_section_count" v-if="activeCases.length > 0">
            {{ activeCases.length }} {{ language === 'cn' ? '个案例' : 'case(s)' }}
          </span>
        </div>

        <div v-if="activeCases.length > 0" class="cases_grid">
          <div
            v-for="(item, index) in activeCases"
            :key="index"
            class="case_card"
            @click="casesDetail(item.DetailPageName, item.DetailFolderName)"
          >
            <div class="case_image_wrap">
              <img :src="item.image" :alt="item.displayName" class="case_image" />
            </div>
            <div class="case_info">
              <h3>{{ item.displayName }}</h3>
              <p>{{ item.description }}</p>
              <span class="case_arrow">→</span>
            </div>
          </div>
        </div>

        <div v-else class="cases_empty">
          <p class="cases_empty_text">
            {{ language === 'cn' ? '敬请期待' : 'Coming Soon' }}
          </p>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import en from "@/data/plan_and_cases_en.js";
import cn from "@/data/plan_and_cases_cn.js";

export default {
  name: "AppPlanandCases",
  data() {
    return {
      activeSector: null,
    };
  },
  mixins: [en, cn],
  computed: {
    ...mapState(["language"]),
    content() {
      const currentLanguage = this.language === "en" ? this.en : this.cn;
      return (
        currentLanguage || {
          title: "",
          sectors: [],
          dropdown_sectors: [],
          dropdown_sectors2: [],
        }
      );
    },
    allCases() {
      const c = this.content;
      return [...(c.dropdown_sectors || []), ...(c.dropdown_sectors2 || [])];
    },
    activeCases() {
      if (!this.activeSector) return [];
      return this.allCases.filter((s) => s.name === this.activeSector);
    },
    activeSectorDisplayName() {
      if (!this.activeSector) return "";
      const sector = this.content.sectors.find(
        (s) => s.name === this.activeSector
      );
      return sector ? sector.displayName : "";
    },
  },
  methods: {
    toggleCases(sectorName) {
      this.activeSector =
        this.activeSector === sectorName ? null : sectorName;
      this.scrollToCases();
    },
    casesDetail(DetailPageName, DetailFolderName) {
      this.$router.push(`/planAndCases/${DetailFolderName}/${DetailPageName}`);
    },
    scrollToCases() {
      this.$nextTick(() => {
        if (this.activeSector && this.$refs.casesSection) {
          this.$refs.casesSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
  },
  watch: {
    language() {
      this.$forceUpdate();
    },
    "$route.query.sector"(newSector) {
      if (newSector) {
        this.activeSector = newSector;
        this.scrollToCases();
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (to.query.sector) {
        vm.activeSector = to.query.sector;
        vm.scrollToCases();
      }
    });
  },
};
</script>

<style scoped>
/* ====== Page header (uses global .page_header) ====== */

.plan_and_cases_home {
  padding: 32px 0 64px;
}

.plan_and_cases_container {
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
}

/* ====== Sector cards (4-grid) ====== */
.plan_and_cases_grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.sector {
  position: relative;
  display: block;
  height: 220px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.sector:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.sector.active {
  border-color: red;
  box-shadow: 0 6px 20px rgba(255, 0, 0, 0.25);
}

.plan_and_cases_img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease, filter 0.3s ease;
}

.sector:hover .plan_and_cases_img {
  transform: scale(1.04);
  filter: brightness(0.85);
}

.sector_overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: white;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.45) 100%
  );
}

.sector_overlay_name {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
}

.sector_overlay_hint {
  font-size: 13px;
  padding: 2px 10px;
  border-radius: 12px;
  background-color: red;
  letter-spacing: 1px;
}

/* ====== Cases section ====== */
.cases_section {
  width: 90%;
  max-width: 1200px;
  margin: 48px auto 0;
  padding: 0;
}

.cases_section_title {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 14px;
  margin-bottom: 28px;
  position: relative;
}

.cases_section_title h2 {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #222;
  margin: 0;
  padding-bottom: 10px;
  position: relative;
}

.cases_section_title h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 4px;
  background-color: red;
}

.cases_section_count {
  font-size: 13px;
  color: #888;
  letter-spacing: 1px;
}

.cases_grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.case_card {
  position: relative;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
}

.case_card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.case_image_wrap {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background-color: #f0f0f0;
}

.case_image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.case_card:hover .case_image {
  transform: scale(1.04);
}

.case_info {
  padding: 18px 20px 22px;
  position: relative;
}

.case_info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #222;
  margin: 0 0 10px;
  line-height: 1.4;
}

.case_info p {
  font-size: 14px;
  line-height: 1.7;
  color: #555;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.case_arrow {
  position: absolute;
  right: 18px;
  bottom: 18px;
  font-size: 18px;
  color: red;
  font-weight: 700;
  transition: transform 0.2s ease;
}

.case_card:hover .case_arrow {
  transform: translateX(4px);
}

/* ====== Empty state ====== */
.cases_empty {
  text-align: center;
  padding: 60px 20px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px dashed #ddd;
}

.cases_empty_text {
  font-size: 1.2rem;
  color: #888;
  letter-spacing: 2px;
  margin: 0;
}

/* ====== Transition ====== */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ====== Responsive ====== */
@media screen and (max-width: 768px) {
  .plan_and_cases_container {
    width: 92%;
  }

  .plan_and_cases_grid {
    grid-template-columns: 1fr;
  }

  .sector {
    height: 160px;
  }

  .sector_overlay_name {
    font-size: 22px;
  }

  .cases_section {
    width: 92%;
    margin-top: 32px;
  }

  .cases_section_title h2 {
    font-size: 1.5rem;
  }

  .cases_grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
</style>
